import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AlertCircle, Clock, X } from 'lucide-react';
import { api } from '../../services/apiService';
import { toastService } from '../../services/toastService';
import AssignPoiModal from './AssignPoiModal';
import SurchargeAgreementsList from './SurchargeAgreementsList';
import { RubyLoader } from '../RubyLoader';
import {
  RegisterState, GrantState, emptyRegister, registerFromResponses, grantsFromGateway,
  buildRegisterPatch, buildGrantPatch, buildBulkFields, countChanges, assignTender, isSurchargeApproved,
  creditMethodForDevice, unknownReaderMessage, hasStoredFlags, REGISTER_EXISTS_STATUSES,
} from './payment-types/model';
import { RegisterPaymentTypes, readerInfoFor } from './payment-types/RegisterPaymentTypes';
import { AdminGrantsCard } from './payment-types/AdminGrantsCard';
import { SaveBar, ErrorBanners, BroadcastSummary, useBroadcast, BulkResult } from './payment-types/SaveBar';
import { saveChanges } from './payment-types/save';
import { RubyTillPayDevicePanel } from './RubyTillPayDevicePanel';
import { isRtpayDevice } from './RubyTillPayRegisterFields';
import { useCatalogModel } from './cardSetup/useCatalogModel';
import { buildCardSetupView } from './cardSetup/PaymentTypesCardSetup';
import { explainPaymentError } from './payment-types/errors';
import { LocationValuesEditor } from './payment-types/LocationValuesEditor';
import { usableTerminalCode } from './TerminalNewCodeModal';
import {
  type LocationValues, type LocationDraft, locationValuesFrom, draftFrom, locationDiff, locationChangeCount,
  locationProblem, buildLocationBody,
} from './payment-types/locationValues';
import {
  readSetup, serverResolved, resolveReaders, readerKindOf, capabilitiesOf,
  type CardSetup, type ResolvedReader, type TipLocation,
} from './cardSetup/cardSetupModel';

// REGISTER_EXISTS_STATUSES lives in ./payment-types/model — shared with the Card Reader card.

interface Props {
  /** `status` lets this skip the fetch entirely for a terminal that has never
   *  been claimed — otherwise every mount fires a request that can only 404. */
  terminal: { terminalId: string; locationId: string; locationName?: string; registerName: string; status?: string; provisionCode?: string; provisionCodeMasked?: boolean; codeExpired?: boolean; registerStatus?: string | null; deviceModelId?: string | null };
  tenantId: string;
  tenantName?: string;
  /** The till's live connection, for the clean save bar. */
  isOnline?: boolean;
  /** The page guards leaving (tabs, back) while this is dirty. */
  onDirtyChange?: (dirty: boolean) => void;
  onNavigateToEmvDevice?: () => void;
  /**
   * SUNMI before any card setup is saved: the tip location picked here is kept
   * by the page and saved with the readers on the Terminal (EMV/SoftPOS) tab.
   */
  pendingTipLocation?: TipLocation | null;
  onPendingTipLocation?: (t: TipLocation) => void;
}

/**
 * THE TERMINAL'S PAYMENT TYPES TAB (Super Admin).
 *
 * The black Admin card (grants), the shared Payment types card, the merchant's
 * location values read-only, and ONE sticky save bar. Nothing saves on toggle;
 * a save sends only what changed, gateway first, then the register, then any
 * broadcast. The server pushes the till — this page never does.
 */
const PosPaymentTypesTab: React.FC<Props> = ({ terminal, tenantId, tenantName, isOnline, onDirtyChange, onNavigateToEmvDevice, pendingTipLocation, onPendingTipLocation }) => {
  const registerId = terminal.terminalId;
  const [base, setBase] = useState<RegisterState>(emptyRegister());
  const [draft, setDraft] = useState<RegisterState>(emptyRegister());
  const [baseGrants, setBaseGrants] = useState<GrantState>({ tipsEnabled: false, cashTipsEnabled: false, surchargeEnabled: false });
  const [grants, setGrants] = useState<GrantState>(baseGrants);
  const [gateway, setGateway] = useState<any | null>(null);
  const [device, setDevice] = useState<any | null>(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showAgreements, setShowAgreements] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [bulkDone, setBulkDone] = useState<BulkResult[] | null>(null);
  /* SUNMI card setup — engaged ONLY when the register's catalog model has a
     cardSetupPolicy AND the register has a stored cardSetup (spec rule 1).
     Otherwise all of this stays null and the tab is exactly as before. */
  const catalog = useCatalogModel(terminal.deviceModelId);
  const policy = catalog.policy;
  const [storedSetup, setStoredSetup] = useState<CardSetup | null>(null);
  const [setupReaders, setSetupReaders] = useState<ResolvedReader[]>([]);
  const [baseTip, setBaseTip] = useState<TipLocation | null>(null);
  const [draftTip, setDraftTip] = useState<TipLocation | null>(null);
  /* The location's tip choices, surcharge rate and card minimum — edited here
     with the merchant's rules, saved through the merchant's own route. */
  const [locBase, setLocBase] = useState<LocationValues>(() => locationValuesFrom(null));
  const [locDraft, setLocDraft] = useState<LocationDraft>(() => draftFrom(locationValuesFrom(null)));
  /** Set when the terminal exists but its register does not (never claimed). */
  const [unclaimed, setUnclaimed] = useState<{ code: string | null; name: string | null } | null>(null);

  const readGateway = useCallback(async () => {
    try { return await api.posNmi.getPaymentGateway(tenantId, terminal.locationId); } catch { return null; }
  }, [tenantId, terminal.locationId]);

  /** `quiet` re-reads after a save without swapping the tab for a loader. */
  const fetchRegister = useCallback(async (quiet?: unknown) => {
    try {
      if (quiet !== true) setLoading(true);
      setError(null);
      /* `registerStatus` is the register row itself. A deregistered till that
         never got the revoke push has a PROVISION row back at `pending` and a
         register still in session — the register wins. */
      if (!terminal.registerStatus && terminal.status && !REGISTER_EXISTS_STATUSES.includes(terminal.status)) {
        setUnclaimed({ code: usableTerminalCode(terminal), name: terminal.registerName || null });
        return;
      }
      const [ptResp, emvResp, gw] = await Promise.all([
        api.posNmi.getPaymentTypes(tenantId, registerId).catch(() => null),
        api.posNmi.getEmvDevice(tenantId, registerId).catch(() => null),
        readGateway(),
      ]);
      /* Only trust the ext3 response if it is the payment-types shape — a
         malformed 200 (an SPA fallback) would otherwise zero every switch. */
      const pt = ptResp && Array.isArray(ptResp.assigned) ? ptResp : null;
      /* The GET says whether surcharge and debit are STORED or following
         (`*Stored`). Only an older GET without those — or no GET at all —
         needs the raw register row (ext2) as the fallback. */
      const regs = hasStoredFlags(pt) ? null : await api.posConfig.listRegisters(tenantId).catch(() => null);
      const raw = (regs?.registers || []).find((r: any) => r.registerId === registerId || r.terminalId === registerId) || null;
      if (!pt && !raw) {
        const t = await api.posTerminals.list(tenantId).catch(() => null);
        const pending = (t?.terminals || []).find((x: any) => x.terminalId === registerId);
        if (pending && pending.status !== 'active') {
          setUnclaimed({ code: usableTerminalCode(pending), name: pending.registerName || null });
          return;
        }
      }
      setUnclaimed(null);
      const src = pt || {
        ...(raw?.paymentTypes || raw?.paymentConfig || {}),
        assigned: (raw?.paymentTypes || raw?.paymentConfig || {}).assigned || (raw?.paymentTypes || raw?.paymentConfig || {}).allowedMethods || [],
      };
      const dev = emvResp?.paired ? emvResp.device : null;
      const reg = registerFromResponses(src, raw, dev?.deviceId || null);
      const g = grantsFromGateway(gw);
      setBase(reg); setDraft(reg);
      setBaseGrants(g); setGrants(g);
      setGateway(gw);
      { const lv = locationValuesFrom(gw); setLocBase(lv); setLocDraft(draftFrom(lv)); }
      setDevice(dev);
      setSaveErrors([]);
      // SUNMI card setup — read only for a model with a policy.
      const setup = policy ? readSetup(pt?.cardSetup, policy) : null;
      setStoredSetup(setup);
      if (setup && policy) {
        let readers = serverResolved(pt?.cardSetupResolved);
        if (!readers) {
          const pool = await api.posNmi.listEmvDevicesAtLocation(tenantId, terminal.locationId).catch(() => null);
          const list: any[] = Array.isArray(pool) ? pool : (pool?.devices || pool?.items || []);
          readers = resolveReaders(setup, list);
        }
        setSetupReaders(readers);
        setBaseTip(setup.tipLocation); setDraftTip(setup.tipLocation);
      } else if (policy) {
        /* SUNMI, no card setup yet: the reader the till uses today (if it is a
           card-setup reader) stands as the primary, so the locks are true. */
        const k = dev ? readerKindOf(dev) : null;
        setSetupReaders(dev && k ? [{ role: 'primary', emvDeviceId: dev.deviceId, kind: k, deviceClass: dev.deviceClass, deviceName: dev.deviceName ?? null, capabilities: capabilitiesOf(dev) }] : []);
        setBaseTip(null); setDraftTip(null);
      } else {
        setSetupReaders([]); setBaseTip(null); setDraftTip(null);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load register data');
    } finally {
      setLoading(false);
    }
  }, [registerId, tenantId, readGateway, terminal.status, terminal.registerStatus, terminal.provisionCode, terminal.registerName, policy, terminal.locationId]);

  useEffect(() => { if (!catalog.loading) fetchRegister(); }, [fetchRegister, catalog.loading]);

  const regPatch = useMemo(() => buildRegisterPatch(base, draft), [base, draft]);
  const grantPatch = useMemo(() => buildGrantPatch(baseGrants, grants), [baseGrants, grants]);
  const bulkFields = useMemo(() => buildBulkFields(base, draft), [base, draft]);
  const tipDirty = !!storedSetup && baseTip !== draftTip;
  const locDiff = useMemo(() => locationDiff(locBase, locDraft), [locBase, locDraft]);
  const locCount = gateway ? locationChangeCount(locDiff) : 0;
  const count = countChanges(regPatch, grantPatch) + (tipDirty ? 1 : 0) + locCount;
  const dirty = count > 0;

  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  const broadcast = useBroadcast({
    tenantId,
    source: { terminalId: registerId, locationId: terminal.locationId },
    fields: bulkFields,
  });

  /** After the agreements modal changes something — keep any unsaved grant edits. */
  const refreshGateway = async () => {
    const gw = await readGateway();
    const g = grantsFromGateway(gw);
    setGateway(gw);
    setBaseGrants(g);
    if (!grantPatch) setGrants(g);
    // Keep unsaved location edits; only refresh the stored baseline.
    const lv = locationValuesFrom(gw);
    setLocBase(lv);
    if (!locCount) setLocDraft(draftFrom(lv));
  };

  const handleDevicePicked = (deviceId: string, deviceClass: string) => {
    const method = creditMethodForDevice({ deviceClass });
    setShowAssignModal(false);
    if (!method) {
      // Never guess a cloud lane for a reader we cannot place.
      setSaveErrors((e) => [...e, unknownReaderMessage({ deviceClass })]);
      return;
    }
    setDraft((d) => assignTender(d, method, deviceId));
  };

  const discard = () => {
    setDraft(base); setGrants(baseGrants); setSaveErrors([]); broadcast.reset();
    setDraftTip(baseTip);
    setLocDraft(draftFrom(locBase));
  };

  const handleSave = async () => {
    const locProblem = locCount ? locationProblem(locDraft) : null;
    if (locProblem) { setSaveErrors([`Tip choices, surcharge rate & card minimum: ${locProblem}`]); return; }
    setSaving(true);
    setSaveErrors([]);
    setBulkDone(null);
    const wide = broadcast.scope !== 'one';
    const outcome = await saveChanges({
      tenantId,
      locationId: terminal.locationId,
      locationName: terminal.locationName,
      grantPatch,
      registers: [{ terminalId: registerId, name: terminal.registerName || 'This till', patch: regPatch }],
      broadcast: wide ? { sourceTerminalId: registerId, run: broadcast.run } : null,
    });
    // What saved becomes the new baseline; what failed stays dirty.
    if (outcome.grantsSaved) setBaseGrants(grants);
    if (outcome.savedTerminals.includes(registerId)) setBase(draft);
    if (outcome.bulkResults) setBulkDone(outcome.bulkResults);
    /* The location's values — the merchant's route, changed keys only, after
       the grants (so a newly granted location takes them). */
    const locBody = locCount ? buildLocationBody(locBase, locDraft) : null;
    if (locBody) {
      try {
        await api.posConfig.putLocationPaymentValues(tenantId, terminal.locationId, locBody as any);
        const gw = await readGateway();
        if (gw) { setGateway(gw); const lv = locationValuesFrom(gw); setLocBase(lv); setLocDraft(draftFrom(lv)); }
      } catch (e) {
        outcome.errors.push(`Tip choices, surcharge rate & card minimum were not saved: ${explainPaymentError(e, 'the service refused the change.')}`);
      }
    }
    /* SUNMI tip location lives on the card setup — sent alone, after the
       register's own fields, so the plain save path is untouched. */
    if (tipDirty && storedSetup && draftTip) {
      try {
        const res = await api.posNmi.putPaymentTypes(tenantId, registerId, { cardSetup: { ...storedSetup, tipLocation: draftTip } });
        if (res?.error) throw Object.assign(new Error(res.message || res.error), { code: res.error, serverMessage: res.message });
        setBaseTip(draftTip);
        setStoredSetup({ ...storedSetup, tipLocation: draftTip });
      } catch (e) {
        outcome.errors.push(`Tips location was not saved: ${explainPaymentError(e, 'the service refused the change.')}`);
      }
    }
    setSaveErrors(outcome.errors);
    if (!outcome.errors.length) {
      broadcast.reset();
      toastService.success(isOnline === false
        ? 'Saved. The till is offline — it picks the changes up when it reconnects.'
        : 'Saved and sent to the till.');
      // Re-read so the effective values (server-side rules) show as stored.
      fetchRegister(true);
    }
    setSaving(false);
  };

  if (loading || catalog.loading) return <RubyLoader variant="page" className="py-16" />;

  if (error) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-8 text-center">
        <AlertCircle size={28} className="mx-auto mb-3 text-red-400" />
        <p className="text-stone-600 text-sm">{error}</p>
        <button onClick={fetchRegister} className="mt-3 min-h-[44px] px-4 text-sm text-ruby hover:text-ruby-deep font-medium">
          Retry
        </button>
      </div>
    );
  }

  if (unclaimed) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-8 text-center">
        <Clock size={28} className="mx-auto mb-3 text-amber-400" />
        <p className="text-stone-800 text-sm font-semibold">This terminal has not been claimed yet</p>
        <p className="mx-auto mt-2 max-w-md text-[12.5px] leading-relaxed text-stone-500">
          {unclaimed.name ? <><span className="font-medium text-stone-700">{unclaimed.name}</span> is provisioned, but </> : 'It is provisioned, but '}
          the device has not entered its code, so it has no register yet. Payment types, tips and card
          configuration are set on the register — there is nothing here to configure until it comes online.
        </p>
        {unclaimed.code ? (
          <p className="mt-3 text-[12.5px] text-stone-500">
            Activation code <span className="rounded bg-stone-100 px-2 py-1 font-mono text-stone-800">{unclaimed.code}</span>
          </p>
        ) : (
          <p className="mt-3 text-[12.5px] text-stone-500">
            Its code has expired or was used — use &quot;Generate new code&quot; on the Terminals list for a fresh one.
          </p>
        )}
        <button onClick={fetchRegister} className="mt-4 min-h-[44px] px-4 text-sm font-medium text-ruby hover:text-ruby-deep">
          Check again
        </button>
      </div>
    );
  }

  const locationName = terminal.locationName || 'this location';
  const reader = !draft.emvDeviceId
    ? { line: 'No card reader paired to this till yet — pick one on the Card Reader tab' }
    : draft.emvDeviceId === device?.deviceId
      ? readerInfoFor(device)
      : { line: 'Card reader on this till · paired when you save' };

  return (
    <div>
      <ErrorBanners errors={saveErrors} onDismiss={(i) => setSaveErrors((e) => e.filter((_, j) => j !== i))} />
      {bulkDone && <BroadcastSummary results={bulkDone} onDismiss={() => setBulkDone(null)} />}

      <AdminGrantsCard
        locationName={locationName}
        draft={draft}
        onChange={setDraft}
        grants={grants}
        onGrantsChange={setGrants}
        gateway={gateway}
        onChangeAgreement={() => setShowAgreements(true)}
        disabled={saving}
      />

      <RegisterPaymentTypes
        locationEditor={(
          <LocationValuesEditor
            gateway={gateway}
            locationName={locationName}
            approved={isSurchargeApproved(gateway, grants)}
            base={locBase}
            draft={locDraft}
            onChange={setLocDraft}
            disabled={saving}
          />
        )}
        draft={draft}
        onChange={setDraft}
        grants={grants}
        onGrantsChange={setGrants}
        gateway={gateway}
        locationName={locationName}
        reader={reader}
        onAssignCredit={() => setShowAssignModal(true)}
        disabled={saving}
        creditDevicePanel={device && isRtpayDevice(device) && draft.emvDeviceId === device.deviceId
          ? <RubyTillPayDevicePanel tenantId={tenantId} device={device} disabled={saving} />
          : null}
        {...(policy && storedSetup && draftTip ? {
          cardSetup: buildCardSetupView({
            policy, readers: setupReaders, tipLocation: draftTip, onTipLocation: setDraftTip,
            onOpenTerminalTab: onNavigateToEmvDevice, disabled: saving,
          }),
        } : policy ? {
          /* SUNMI, nothing saved yet — the same section in its "no reader yet" state. */
          cardSetup: buildCardSetupView({
            policy, readers: setupReaders, pending: true,
            tipLocation: pendingTipLocation || (policy.customerDisplay ? 'customer_display' : 'terminal'),
            onTipLocation: (t) => onPendingTipLocation?.(t),
            onOpenTerminalTab: onNavigateToEmvDevice, disabled: saving || !onPendingTipLocation,
          }),
        } : {})}
      />
      {policy && onNavigateToEmvDevice && (
        <p className="-mt-2 mb-3 px-1 text-xs text-stone-500">
          The readers themselves are managed on the{' '}
          <button type="button" onClick={onNavigateToEmvDevice} className="min-h-[44px] font-medium text-ruby hover:text-ruby-deep">
            Terminal (EMV/SoftPOS) tab
          </button>.
        </p>
      )}
      {!policy && onNavigateToEmvDevice && (
        <p className="-mt-2 mb-3 px-1 text-xs text-stone-500">
          The reader itself is managed on the{' '}
          <button type="button" onClick={onNavigateToEmvDevice} className="min-h-[44px] font-medium text-ruby hover:text-ruby-deep">
            Card Reader tab
          </button>.
        </p>
      )}

      <SaveBar
        count={count}
        saving={saving}
        onSave={handleSave}
        onDiscard={discard}
        broadcast={broadcast}
        locationName={locationName}
        tenantName={tenantName}
        locationWide={locCount > 0 || (!!grantPatch && (grantPatch.tipsEnabled !== undefined || grantPatch.cashTipsEnabled !== undefined || grantPatch.surchargeEnabled !== undefined))}
        cleanStatus={isOnline === undefined ? null : (
          <span className="inline-flex items-center gap-1.5">
            <span className={`h-2 w-2 rounded-full ${isOnline ? 'bg-green-600' : 'bg-amber-500'}`} />
            {isOnline ? 'Till online — changes reach it as soon as they are saved' : 'Till offline — your changes apply when it reconnects'}
          </span>
        )}
      />

      {showAssignModal && (
        <AssignPoiModal
          tenantId={tenantId}
          locationId={terminal.locationId}
          terminalId={registerId}
          terminalName={terminal.registerName}
          currentDeviceId={device?.deviceId || null}
          onPicked={handleDevicePicked}
          onClose={() => setShowAssignModal(false)}
          onGoRegister={() => {
            setShowAssignModal(false);
            if (onNavigateToEmvDevice) onNavigateToEmvDevice();
          }}
        />
      )}

      {showAgreements && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setShowAgreements(false)}>
          <div role="dialog" aria-modal="true" aria-label="Surcharge agreements" className="max-h-[85vh] w-full max-w-[640px] overflow-y-auto rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-stone-200 px-5 py-3">
              <h3 className="text-[15px] font-semibold text-stone-900">Surcharge agreements · {locationName}</h3>
              <button type="button" aria-label="Close" onClick={() => setShowAgreements(false)} className="grid h-11 w-11 place-items-center rounded-lg text-stone-500 hover:bg-stone-100">
                <X size={16} />
              </button>
            </div>
            <div className="p-5">
              <SurchargeAgreementsList
                tenantId={tenantId}
                locationId={terminal.locationId}
                agreements={Array.isArray(gateway?.surchargeAgreements) ? gateway.surchargeAgreements : []}
                activeAgreementId={gateway?.activeSurchargeAgreementId || null}
                onChanged={refreshGateway}
                standalone
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PosPaymentTypesTab;
